import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useArchData } from "../hooks/useArchData";
import { DentalViewport } from "../components/dental/DentalViewport";
import { CrownEditorViewport } from "../components/dental/CrownEditorViewport";
import { SplitViewContainer } from "../app/layout/SplitViewContainer";
import { useCaseSession } from "../state/CaseSessionContext";
import { saveDentalCaseData } from "../services/dentalArchService";
import { solveCrownBasic } from "../services/wasm/restorationWasmService";
import { PrescriptionListPanel } from "../components/prescription/PrescriptionListPanel";
import { ProcedureToolbox } from "../components/toolbox/ProcedureToolbox";
import { ViewportToolbar } from "../components/toolbox/ViewportToolbar";
import { DebugPanel } from "../components/toolbox/DebugPanel";
import type { RestorationPlan, ViewMode, JawType } from "../types/dental";
import type { CrownSolveResult, ToothContext } from "../types/restoration";

import type {
  ToothPlacementAdjustment,
  ToothAdjustmentMap,
} from "../types/dental";
import { DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT } from "../types/dental";

import type {
  ViewportCommand,
  ViewPreset,
} from "../types/dental";

interface GeneratedCrownPreview {
  caseId: string;
  prescriptionItemId: string;
  planId: string;
  jawType: JawType;
  targetToothId: string;
  result: CrownSolveResult;
}

type CrownVisibilityMode = "active_only" | "all_visible";
type GeneratedCrownPreviewMap = Record<string, GeneratedCrownPreview>;
type InteractionMode = "normal" | "pick_restoration_for_edit";
type GeometryDisplayMode = "blocks" | "realistic";

function ToothInspectionPanel({
  selectedToothId,
  selectedPrescriptionItem,
  onClear,
}: any) {
  if (!selectedToothId) {
    return (
      <div className="card panel">
        <div className="panel-title">Tooth Inspection</div>
        <div className="panel-empty">Click a tooth to inspect</div>
      </div>
    );
  }

  const isInPrescription =
    selectedPrescriptionItem?.target.teeth.includes(selectedToothId) ?? false;

  return (
    <div className="card panel">
      <div className="panel-title">Tooth Inspection</div>

      <div className="panel-row">
        <span>Tooth</span>
        <strong>{selectedToothId}</strong>
      </div>

      <div className="panel-row">
        <span>In Prescription</span>
        <strong>{isInPrescription ? "Yes" : "No"}</strong>
      </div>

      <div className="panel-actions">
        <button className="secondary-button" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}

function applyCrownPlacementRefinement(params: {
  result: CrownSolveResult;
  targetContext: ToothContext;
  jawType: JawType;
}): CrownSolveResult {
  const { result, targetContext, jawType } = params;

  const [tx, ty, tz] = targetContext.center;
  const [, toothHeight] = targetContext.size;
  const [, crownHeight] = result.dimensions;
  const rotationEuler = targetContext.rotationEuler ?? result.rotationEuler;

  const euler = new THREE.Euler(
    rotationEuler[0],
    rotationEuler[1],
    rotationEuler[2]
  );

  const quaternion = new THREE.Quaternion().setFromEuler(euler);

  const localHeightAxis = new THREE.Vector3(0, 1, 0).applyQuaternion(quaternion);
  const outwardSign = jawType === "upper" ? 1 : -1;

  const seatOffset = toothHeight * 0.5 - crownHeight * 0.5;
  const visualLift = 1.5;
  const offsetDistance = outwardSign * (seatOffset + visualLift);

  const refinedCenter = new THREE.Vector3(tx, ty, tz).add(
    localHeightAxis.multiplyScalar(offsetDistance)
  );

  return {
    ...result,
    center: [refinedCenter.x, refinedCenter.y, refinedCenter.z],
    rotationEuler,
  };
}

export function ArchDesignerPage() {
  const {
    currentCase,
    editableJaws,
    editablePrescriptionItems,
    editableRestorationPlans,
    selectedCaseId,
    activeJaw,
    viewMode,
    setCaseData,
    setViewMode,
    setSelectedPrescriptionItemId,
    selectedPrescriptionItemId,
    updateActiveJawArch,
    addRestorationPlan,
    updateRestorationPlan,
    isDirty,
    isSaving,
    saveError,
    beginSave,
    completeSaveSuccess,
    completeSaveFailure,
  } = useCaseSession();

  const [focusNonce, setFocusNonce] = useState(0);
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [selectedToothId, setSelectedToothId] = useState<string | null>(null);
  const [isGeneratingCrown, setIsGeneratingCrown] = useState(false);
  const [jawOpenAngleDeg, setJawOpenAngleDeg] = useState(0);
  const [generatedCrownPreviews, setGeneratedCrownPreviews] =
    useState<GeneratedCrownPreviewMap>({});
  const [crownVisibilityMode, setCrownVisibilityMode] =
    useState<CrownVisibilityMode>("active_only");

  const [interactionMode, setInteractionMode] =
    useState<InteractionMode>("normal");
  const [selectedRestorationPlanId, setSelectedRestorationPlanId] =
    useState<string | null>(null);
  const [isRestorationEditMode, setIsRestorationEditMode] = useState(false);
  const [geometryDisplayMode, setGeometryDisplayMode] =
  useState<GeometryDisplayMode>("blocks");

  const [viewportCommand, setViewportCommand] = useState<ViewportCommand | null>(null);


const [toothAdjustments, setToothAdjustments] =
  useState<ToothAdjustmentMap>({});

const selectedToothAdjustment: ToothPlacementAdjustment = selectedToothId
  ? toothAdjustments[selectedToothId] ?? DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT
  : DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT;

 

  const debugAnchorRef = useRef<HTMLDivElement | null>(null);
  const toothContextProviderRef = useRef<
    ((toothId: string) => ToothContext | null) | null
  >(null);

  const { data, isLoading, error } = useArchData(selectedCaseId);

  useEffect(() => {
    if (!data || !selectedCaseId) return;

    if (!currentCase || currentCase.summary.caseId !== data.summary.caseId) {
      setCaseData(data);
    }
  }, [data, selectedCaseId, currentCase, setCaseData]);

  const selectedPrescriptionItem = useMemo(() => {
    if (!selectedPrescriptionItemId) return null;

    return (
      editablePrescriptionItems.find(
        (item) => item.itemId === selectedPrescriptionItemId
      ) || null
    );
  }, [editablePrescriptionItems, selectedPrescriptionItemId]);

  const selectedRestorationPlan = useMemo(() => {
    if (!selectedPrescriptionItemId) return null;

    return (
      editableRestorationPlans.find(
        (plan) => plan.prescriptionItemId === selectedPrescriptionItemId
      ) || null
    );
  }, [editableRestorationPlans, selectedPrescriptionItemId]);

  const activeEditingRestorationPlan = useMemo(() => {
    if (!selectedRestorationPlanId) {
      return null;
    }

    return (
      editableRestorationPlans.find(
        (plan) => plan.planId === selectedRestorationPlanId
      ) || null
    );
  }, [editableRestorationPlans, selectedRestorationPlanId]);

  const activeEditingCrownPreview = useMemo(() => {
    if (!selectedRestorationPlanId) {
      return null;
    }

    return generatedCrownPreviews[selectedRestorationPlanId] ?? null;
  }, [generatedCrownPreviews, selectedRestorationPlanId]);

  const editorContextToothIds = useMemo(() => {
    const ids = new Set<string>();

    if (activeEditingCrownPreview?.targetToothId) {
      ids.add(activeEditingCrownPreview.targetToothId);
    }

    for (const toothId of activeEditingRestorationPlan?.targetTeeth ?? []) {
      ids.add(toothId);
    }

    return Array.from(ids);
  }, [activeEditingCrownPreview, activeEditingRestorationPlan]);

  const visibleGeneratedCrownResults = useMemo(() => {
    if (!currentCase) {
      return [];
    }

    return Object.values(generatedCrownPreviews)
      .filter((preview) => {
        const isSameCase = preview.caseId === currentCase.summary.caseId;
        const isJawVisible =
          viewMode === "both" || viewMode === preview.jawType;

        if (!isSameCase || !isJawVisible) {
          return false;
        }

        if (crownVisibilityMode === "all_visible") {
          return true;
        }

        if (!selectedRestorationPlan || !selectedPrescriptionItem) {
          return false;
        }

        return (
          preview.planId === selectedRestorationPlan.planId &&
          preview.prescriptionItemId === selectedPrescriptionItem.itemId
        );
      })
      .map((preview) => ({
        ...preview.result,
        metadata: {
          ...preview.result.metadata,
          planId: preview.planId,
          prescriptionItemId: preview.prescriptionItemId,
          targetToothId: preview.targetToothId,
          restorationType: "crown" as const,
        },
      }));
  }, [
    generatedCrownPreviews,
    currentCase,
    viewMode,
    crownVisibilityMode,
    selectedRestorationPlan,
    selectedPrescriptionItem,
  ]);

  useEffect(() => {
    if (!selectedPrescriptionItem) return;

    const targetJaw = selectedPrescriptionItem.target.jaw;

    const nextViewMode: ViewMode =
      viewMode === "both"
        ? "both"
        : viewMode === targetJaw
          ? viewMode
          : targetJaw;

    if (nextViewMode !== viewMode) {
      setViewMode(nextViewMode);
    }

    setFocusNonce((v) => v + 1);
  }, [selectedPrescriptionItem, viewMode, setViewMode]);

useEffect(() => {
  setGeneratedCrownPreviews({});
  setInteractionMode("normal");
  setSelectedRestorationPlanId(null);
  setIsRestorationEditMode(false);
  setSelectedToothId(null);
  setToothAdjustments({});
}, [selectedCaseId]);

  async function handleSave() {
    if (!currentCase || !editableJaws || isSaving) return;

    try {
      beginSave();
      await saveDentalCaseData(
        currentCase.summary.caseId,
        editableJaws,
        editableRestorationPlans
      );
      completeSaveSuccess();
    } catch (err) {
      completeSaveFailure(
        err instanceof Error ? err.message : "Failed to save case."
      );
    }
  }

  function handleCreatePlan() {
    if (!selectedPrescriptionItem) return;

    const existing = editableRestorationPlans.find(
      (plan) => plan.prescriptionItemId === selectedPrescriptionItem.itemId
    );

    if (existing) return;

    const newPlan: RestorationPlan = {
      planId: `rp-${Date.now()}`,
      prescriptionItemId: selectedPrescriptionItem.itemId,
      title: `Plan for ${selectedPrescriptionItem.title}`,
      procedureType: selectedPrescriptionItem.procedureType,
      status: "not_started",
      targetJaw: selectedPrescriptionItem.target.jaw,
      targetTeeth: [...selectedPrescriptionItem.target.teeth],
      selectedCatalogItem: "",
      material: "",
      shade: "",
      spacingNotes: "",
      designNotes: "",
      productionNotes: "",
    };

    addRestorationPlan(newPlan);
  }


function handleSetSelectedToothRotationDeg(
  axisIndex: 0 | 1 | 2,
  value: number
) {
  if (!selectedToothId) return;

  setToothAdjustments((prev) => {
    const current =
      prev[selectedToothId] ?? DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT;

    const nextRotation: [number, number, number] = [...current.rotationDeg];
    nextRotation[axisIndex] = value;

    return {
      ...prev,
      [selectedToothId]: {
        rotationDeg: nextRotation,
        translation: current.translation,
      },
    };
  });
}

function handleSetSelectedToothTranslation(
  axisIndex: 0 | 1 | 2,
  value: number
) {
  if (!selectedToothId) return;

  setToothAdjustments((prev) => {
    const current =
      prev[selectedToothId] ?? DEFAULT_TOOTH_PLACEMENT_ADJUSTMENT;

    const nextTranslation: [number, number, number] = [
      ...current.translation,
    ];
    nextTranslation[axisIndex] = value;

    return {
      ...prev,
      [selectedToothId]: {
        rotationDeg: current.rotationDeg,
        translation: nextTranslation,
      },
    };
  });
}

function handleResetSelectedToothAdjustment() {
  if (!selectedToothId) return;

  setToothAdjustments((prev) => ({
    ...prev,
    [selectedToothId]: {
      rotationDeg: [0, 0, 0],
      translation: [0, 0, 0],
    },
  }));
}

  function handleBeginPickRestorationForEdit() {
    setInteractionMode("pick_restoration_for_edit");
    setIsRestorationEditMode(false);
    setSelectedRestorationPlanId(null);
  }

  function handleCancelPickRestorationForEdit() {
    setInteractionMode("normal");
  }

  function handleRestorationPickForEdit(planId: string) {
    setSelectedRestorationPlanId(planId);
    setInteractionMode("normal");
    setIsRestorationEditMode(true);
  }

  function getNeighborToothId(
    targetToothId: string,
    offset: -1 | 1,
    selectedPlan: RestorationPlan
  ): string | null {
    const targetTeeth = selectedPlan.targetTeeth;
    const indexInPlan = targetTeeth.indexOf(targetToothId);

    if (indexInPlan >= 0) {
      const neighbor = targetTeeth[indexInPlan + offset];
      return neighbor || null;
    }

    const match = /^T(\d+)$/.exec(targetToothId);
    if (!match) {
      return null;
    }

    const numeric = Number(match[1]);
    const next = numeric + offset;

    if (next <= 0) {
      return null;
    }

    return `T${next}`;
  }

  function getToothContextOrNull(toothId: string | null): ToothContext | null {
    if (!toothId || !toothContextProviderRef.current) {
      return null;
    }

    return toothContextProviderRef.current(toothId);
  }

  async function handleTestWasm() {
    try {
      const result = await solveCrownBasic({
        jawType: activeJaw,
        target: {
          toothId: selectedToothId || "11",
          center: [10, 20, 30],
          size: [8, 10, 7],
        },
        leftNeighbor: {
          toothId: "10",
          center: [2, 20, 30],
          size: [7.5, 9.8, 6.8],
        },
        rightNeighbor: {
          toothId: "12",
          center: [18, 20, 30],
          size: [7.7, 10.1, 6.9],
        },
      });

      console.log("[DCube][WASM] crown solve result:", result);

      alert(
        result.success
          ? `WASM success\nCenter: ${result.center.join(", ")}\nDimensions: ${result.dimensions.join(", ")}`
          : `WASM failed: ${result.message || "Unknown error"}`
      );
    } catch (error) {
      console.error("[DCube][WASM] crown solve failed:", error);

      alert(
        error instanceof Error
          ? `WASM runtime error: ${error.message}`
          : "Unknown WASM runtime error"
      );
    }
  }

  async function handleGenerateCrown() {
    if (!selectedRestorationPlan || !selectedPrescriptionItem || !currentCase) {
      alert(
        "Please select a crown prescription and create a restoration plan first."
      );
      return;
    }

    if (selectedPrescriptionItem.procedureType !== "crown") {
      alert("Generate Crown is only available for crown procedures.");
      return;
    }

    const targetToothId =
      selectedToothId ||
      selectedRestorationPlan.targetTeeth[0] ||
      selectedPrescriptionItem.target.teeth[0] ||
      null;

    if (!targetToothId) {
      alert("No target tooth is available for crown generation.");
      return;
    }

    const targetContext = getToothContextOrNull(targetToothId);
    if (!targetContext) {
      alert(`Unable to resolve tooth context for ${targetToothId}.`);
      return;
    }

    const leftNeighborId = getNeighborToothId(
      targetToothId,
      -1,
      selectedRestorationPlan
    );
    const rightNeighborId = getNeighborToothId(
      targetToothId,
      1,
      selectedRestorationPlan
    );

    const leftNeighbor = getToothContextOrNull(leftNeighborId);
    const rightNeighbor = getToothContextOrNull(rightNeighborId);

    try {
      setIsGeneratingCrown(true);

      const result = await solveCrownBasic({
        jawType: activeJaw,
        target: targetContext,
        leftNeighbor,
        rightNeighbor,
      });

      console.log("[DCube][WASM] generate crown result:", result);

      if (!result.success) {
        alert(`Generate Crown failed: ${result.message || "Unknown error"}`);
        return;
      }

      const refinedResult = applyCrownPlacementRefinement({
        result: {
          ...result,
          rotationEuler: targetContext.rotationEuler ?? result.rotationEuler,
        },
        targetContext,
        jawType: activeJaw,
      });

      setGeneratedCrownPreviews((prev) => ({
        ...prev,
        [selectedRestorationPlan.planId]: {
          caseId: currentCase.summary.caseId,
          prescriptionItemId: selectedPrescriptionItem.itemId,
          planId: selectedRestorationPlan.planId,
          jawType: activeJaw,
          targetToothId,
          result: {
            ...refinedResult,
            metadata: {
              ...refinedResult.metadata,
              planId: selectedRestorationPlan.planId,
              prescriptionItemId: selectedPrescriptionItem.itemId,
              targetToothId,
              restorationType: "crown",
            },
          },
        },
      }));
    } catch (error) {
      console.error("[DCube][WASM] generate crown failed:", error);

      alert(
        error instanceof Error
          ? `Generate Crown runtime error: ${error.message}`
          : "Unknown crown generation error"
      );
    } finally {
      setIsGeneratingCrown(false);
    }
  }

  if (isLoading && !editableJaws) {
    return (
      <div className="page">
        <div className="card panel">Loading case...</div>
      </div>
    );
  }

  if (error && !editableJaws) {
    return (
      <div className="page">
        <div className="card panel">
          Failed to load case:{" "}
          {String((error as { message?: string } | null)?.message ?? error)}
        </div>
      </div>
    );
  }

  const showEditorViewport =
    isRestorationEditMode && Boolean(activeEditingCrownPreview?.result?.success);

  return (
    <div className="page">
      <div className="workbench-layout">
        <div className="workbench-layout__left">
          <PrescriptionListPanel
            items={editablePrescriptionItems}
            selectedItemId={selectedPrescriptionItemId}
            onSelectItem={setSelectedPrescriptionItemId}
          />

          <ToothInspectionPanel
            selectedToothId={selectedToothId}
            selectedPrescriptionItem={selectedPrescriptionItem}
            onClear={() => setSelectedToothId(null)}
          />
        </div>

        <div className="workbench-layout__center">
          <div className="card">
            <div className="debug-anchor" ref={debugAnchorRef}>
              <ViewportToolbar
                prescriptionItem={selectedPrescriptionItem}
                plan={selectedRestorationPlan}
                viewMode={viewMode}
                isDirty={isDirty}
                isSaving={isSaving}
                isDebugPanelOpen={isDebugPanelOpen}
                crownVisibilityMode={crownVisibilityMode}
                interactionMode={interactionMode}
                geometryDisplayMode={geometryDisplayMode}
                hasAnyRestoration={Object.keys(generatedCrownPreviews).length > 0}
                jawOpenAngleDeg={jawOpenAngleDeg}
                selectedToothId={selectedToothId}
                selectedToothAdjustment={selectedToothAdjustment}
                onSetSelectedToothRotationDeg={handleSetSelectedToothRotationDeg}
                onSetSelectedToothTranslation={handleSetSelectedToothTranslation}
                onResetSelectedToothAdjustment={handleResetSelectedToothAdjustment}
                onCreatePlan={handleCreatePlan}
                onSave={() => void handleSave()}
                onSetViewMode={setViewMode}
                onSetCrownVisibilityMode={setCrownVisibilityMode}
                onSetGeometryDisplayMode={setGeometryDisplayMode}
                onSetJawOpenAngleDeg={setJawOpenAngleDeg}
                onFocusTarget={() => setFocusNonce((v) => v + 1)}
                onToggleDebugPanel={() => setIsDebugPanelOpen((v) => !v)}
                onBeginPickRestorationForEdit={handleBeginPickRestorationForEdit}
                onCancelPickRestorationForEdit={handleCancelPickRestorationForEdit}
                onFitToView={() =>
                  setViewportCommand({
                    type: "fit",
                    nonce: Date.now(),
                  })
                }
                onZoomIn={() =>
                  setViewportCommand({
                    type: "zoomIn",
                    nonce: Date.now(),
                  })
                }
                onZoomOut={() =>
                  setViewportCommand({
                    type: "zoomOut",
                    nonce: Date.now(),
                  })
                }
                onSetViewPreset={(preset) =>
                  setViewportCommand({
                    type: "view",
                    preset,
                    nonce: Date.now(),
                  })
                }
              />

              <DebugPanel
                isOpen={isDebugPanelOpen}
                activeJaw={activeJaw}
                editableJaws={editableJaws}
                onClose={() => setIsDebugPanelOpen(false)}
                onUpdateActiveJawArch={updateActiveJawArch}
                onTestWasm={handleTestWasm}
              />
            </div>

            {isRestorationEditMode && activeEditingRestorationPlan && (
              <div className="panel-row" style={{ marginBottom: 12 }}>
                <span>Editing Restoration</span>
                <strong>{activeEditingRestorationPlan.title}</strong>
              </div>
            )}

            {saveError && (
              <div className="panel-error" role="alert">
                {saveError}
              </div>
            )}

            {editableJaws && (
              <SplitViewContainer
                bottomVisible={showEditorViewport}
                top={
                  <DentalViewport
                    upper={editableJaws.upper}
                    lower={editableJaws.lower}
                    viewMode={viewMode}
                    selectedPrescriptionItem={selectedPrescriptionItem}
                    selectedToothId={selectedToothId}
                    onToothSelect={setSelectedToothId}
                    focusNonce={focusNonce}
                    toothAdjustments={toothAdjustments}
                    generatedCrownResults={visibleGeneratedCrownResults}
                    interactionMode={interactionMode}
                    geometryDisplayMode={geometryDisplayMode}
                    jawOpenAngleDeg={jawOpenAngleDeg}
                    viewportCommand={viewportCommand}                    
                    onRestorationPickForEdit={handleRestorationPickForEdit}
                    onRegisterToothContextProvider={(provider) => {
                      toothContextProviderRef.current = provider;
                    }}
                  />
                }
                bottom={
                  <CrownEditorViewport
                    upper={editableJaws.upper}
                    lower={editableJaws.lower}
                    viewMode={activeEditingCrownPreview?.jawType ?? activeJaw}
                    restoration={activeEditingCrownPreview?.result ?? null}
                    contextToothIds={editorContextToothIds}
                  />
                }
              />
            )}
          </div>
        </div>

        <div className="workbench-layout__right">
          <ProcedureToolbox
            prescriptionItem={selectedPrescriptionItem}
            plan={selectedRestorationPlan}
            onCreatePlan={handleCreatePlan}
            onUpdatePlan={updateRestorationPlan}
            onGenerateCrown={handleGenerateCrown}
            isGeneratingCrown={isGeneratingCrown}
          />
        </div>
      </div>
    </div>
  );
}